// Clip analysis — three paths, same prompt + contract ({ video_clips:[...] }):
//   • analyzeViaProxy  → zero-key OpenRouter, routed through /yt/or (server key or BYOK header).
//                        Thin wrapper over analyzeViaLlm (preset 'openrouter', empty key).
//   • analyzeViaLlm    → OmniRoute / 9Router / any OpenAI-compatible preset (or custom),
//                        via lib/llm/chat.js (local routers browser-direct, hosted via the bridge).
//   • analyzeViaGemini → Gemini, called DIRECT from the browser with the user's key
//                        (generativelanguage.googleapis.com allows CORS → key never transits our server).
import { GoogleGenerativeAI } from '@google/generative-ai';
import { generateWithFallback } from '../lib/engine/geminiModels.js';
import { chatCompletion } from '../lib/llm/chat.js';
import { getPreset } from '../lib/llm/registry.js';

// OpenRouter slug used as THIS product's default/fallback model (GA, cheap; 2.0-flash-001
// retired/404). Spec's cheap/strong defaults are provisional fallbacks for other presets —
// this one is pinned deliberately, so it's exported for Lite.jsx's getLlm() to reuse.
export const ANALYZE_MODEL = 'google/gemini-3.1-flash-lite';
const GEMINI_MODEL = 'gemini-3.1-flash-lite';         // direct generativelanguage API slug (GA, browser-callable; 2.5-flash retiring ~Oct 2026)

function extractJson(text) {
  const t = String(text).trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  try { return JSON.parse(t); } catch { /* fall through */ }
  const a = t.indexOf('{'), b = t.lastIndexOf('}');
  if (a !== -1 && b > a) { try { return JSON.parse(t.slice(a, b + 1)); } catch { /* noop */ } }
  return null;
}

function buildPrompt({ segments = [], language = 'id', minDuration = 20, maxDuration = 60, numCuts, instruction } = {}) {
  const transcript = segments.map((s) => `[${Math.round(s.start)}s] ${s.text}`).join('\n')
    || '(no transcript — infer pacing from title)';
  const lang = language === 'en' ? 'English' : 'Bahasa Indonesia';
  const custom = instruction && String(instruction).trim()
    ? `\nUSER INSTRUCTION (highest priority — follow this when picking moments): ${String(instruction).trim()}\n`
    : '';
  return `You are a viral short-form video editor. From the transcript below, pick the ${numCuts || 'best 3-6'} most engaging moments to cut into Reels/TikTok/Shorts clips.
${custom}
Return ONLY valid JSON (no markdown fence), an object of this exact shape:
{
  "video_clips": [
    { "clip_id": "1", "start": <seconds number>, "end": <seconds number>, "topic": "<short title>", "reason": "<why this clips well>", "hook": "<punchy on-screen headline, ${lang}, max 8 words>", "hashtags": ["#tag1", "#tag2", "#tag3"] }
  ]
}

Rules:
- start/end are seconds (numbers).
- Each clip MUST be ${minDuration}-${maxDuration} seconds long. AIM for 30-45s — a COMPLETE self-contained moment (setup → point → payoff), never a tiny ${minDuration}s-or-shorter fragment.
- Extend the boundaries to include the lead-in and the resolution so the clip makes sense on its own.
- hashtags: 4-6 relevant, high-reach tags for this clip's topic (${lang} + niche), each starting with #, no spaces.
- Ordered by virality, no overlaps.

TRANSCRIPT:
${transcript}`;
}

function normalizeClips(arr, minDuration, maxDuration) {
  return arr.map((c, i) => {
    const start = Number(c.start) || 0;
    let end = Number(c.end) || start + maxDuration;
    if (end <= start) end = start + maxDuration;
    if (end - start < minDuration) end = start + minDuration;
    if (end - start > maxDuration) end = start + maxDuration;
    const hashtags = Array.isArray(c.hashtags)
      ? c.hashtags.map((h) => String(h).trim()).filter(Boolean).map((h) => (h.startsWith('#') ? h : '#' + h)).slice(0, 8)
      : [];
    return {
      clip_id: String(c.clip_id ?? i + 1), start, end,
      topic: c.topic || `Clip ${i + 1}`, reason: c.reason || '', hook: c.hook || c.topic || '', hashtags,
    };
  });
}

// OmniRoute / 9Router / any OpenAI-compatible preset (or custom base URL). `llm` is
// the resolved {presetId, baseUrl, apiKey, model} — Lite.jsx's getLlm() builds it.
// videoTitle kept in the signature for call-site symmetry with analyzeViaGemini/
// analyzeViaProxy (same options object shape across all three); unused here.
// eslint-disable-next-line no-unused-vars
export async function analyzeViaLlm({ llm, segments = [], videoTitle = 'Qlipper Web', language = 'id', minDuration = 20, maxDuration = 60, numCuts, instruction } = {}) {
  const { presetId, baseUrl, apiKey, model } = llm || {};
  const preset = getPreset(presetId);
  const prompt = buildPrompt({ segments, language, minDuration, maxDuration, numCuts, instruction });
  const { content } = await chatCompletion({
    presetId,
    baseUrl: baseUrl || preset?.baseUrl,
    apiKey,
    model: model || preset?.defaultModels?.cheap,
    messages: [{ role: 'user', content: prompt }],
  });
  const parsed = extractJson(content || '');
  if (!parsed || !Array.isArray(parsed.video_clips)) {
    throw new Error('AI gagal menganalisis (jawaban tidak terbaca). Coba lagi.');
  }
  return { video_clips: normalizeClips(parsed.video_clips, minDuration, maxDuration) };
}

// Zero-key default: OpenRouter preset, empty key → chatCompletion routes it through
// /yt/or with no X-Byok-Key, so the bridge falls back to the server-side OPENROUTER_KEY
// exactly as before (free_enabled gate unchanged).
export async function analyzeViaProxy({ segments = [], videoTitle = 'Qlipper Web', language = 'id', minDuration = 20, maxDuration = 60, numCuts, instruction } = {}) {
  const preset = getPreset('openrouter');
  return analyzeViaLlm({
    llm: { presetId: 'openrouter', baseUrl: preset.baseUrl, apiKey: '', model: ANALYZE_MODEL },
    segments, videoTitle, language, minDuration, maxDuration, numCuts, instruction,
  });
}

export async function analyzeViaGemini({ apiKey, segments = [], videoTitle = 'Qlipper Web', language = 'id', minDuration = 20, maxDuration = 60, numCuts, instruction } = {}) {
  if (!apiKey) throw new Error('Gemini API key kosong.');
  const prompt = buildPrompt({ segments, language, minDuration, maxDuration, numCuts, instruction });
  let text;
  try {
    // Preflight + fallback walk (shared resolver) — a retiring/404 model id no
    // longer bricks the analysis; key-level errors surface fast. See geminiModels.js.
    const genAI = new GoogleGenerativeAI(apiKey);
    const { result } = await generateWithFallback(apiKey, GEMINI_MODEL, (modelId) =>
      genAI.getGenerativeModel({ model: modelId }).generateContent(prompt)
    );
    text = result.response.text();
  } catch (e) {
    throw new Error('Gemini gagal: ' + (e?.message || e));
  }
  const parsed = extractJson(text);
  if (!parsed || !Array.isArray(parsed.video_clips)) {
    throw new Error('Gemini: jawaban tidak terbaca. Coba lagi.');
  }
  return { video_clips: normalizeClips(parsed.video_clips, minDuration, maxDuration) };
}
